import { useCallback, useEffect, useMemo } from "react";

interface UseTablePageSizeProps {
  tableName: string;
  total: number;
  pageSize: number | undefined;
  setPageSize: (pageSize: number) => void;
  maxPageSize?: number;
}

interface UseTablePageSizeReturn {
  pageSizeOptions: number[];
  currentPageSize: number;
  onPageSizeChange: (pageSize: number) => void;
}

export const useTablePageSize = ({
  tableName,
  total,
  pageSize,
  setPageSize,
  maxPageSize = 300,
}: UseTablePageSizeProps): UseTablePageSizeReturn => {
  const storageKey = `${tableName}PageSize`;

  const getStoredPageSize = useCallback((): number | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;
      const parsed = Number(JSON.parse(stored));
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.min(parsed, maxPageSize);
    } catch {
      return null;
    }
  }, [storageKey, maxPageSize]);

  const setStoredPageSize = useCallback(
    (value: number): void => {
      localStorage.setItem(storageKey, JSON.stringify(value));
    },
    [storageKey]
  );

  useEffect(() => {
    const storedPageSize = getStoredPageSize();
    if (storedPageSize) {
      setPageSize(storedPageSize);
    }
  }, [getStoredPageSize, setPageSize]);

  const currentPageSize = pageSize ?? (total > 15 ? 15 : total);

  const pageSizeOptions = useMemo(() => {
    if (total <= 15) return [];
    const options: number[] = [];
    if (total > 15) options.push(15);
    if (total > 25) options.push(25);
    if (total > 50) options.push(50);
    if (total > 150) options.push(150);
    if (total > 300) options.push(300);

    const maxOption = Math.min(total, maxPageSize);
    if (maxOption > 15 && !options.includes(maxOption)) {
      options.push(maxOption);
    }

    if (currentPageSize && !options.includes(currentPageSize)) {
      options.push(currentPageSize);
    }

    return options.sort((a, b) => a - b);
  }, [total, maxPageSize, currentPageSize]);

  const onPageSizeChange = useCallback(
    (value: number) => {
      const next = Math.min(value, maxPageSize);
      setPageSize(next);
      setStoredPageSize(next);
    },
    [maxPageSize, setPageSize, setStoredPageSize]
  );

  return {
    pageSizeOptions,
    currentPageSize,
    onPageSizeChange,
  };
};
